import React, { SyntheticEvent } from 'react';
import { css } from '@emotion/core';
import Pagination from '@atlaskit/pagination';
import { PageCapabilityDTO } from '@atlassiansox/phobos-client-ts';
import { useQuery } from '@tanstack/react-query';
import Spinner from '@atlaskit/spinner';
import { token } from '@atlaskit/tokens';
import { UFOExperience } from '@atlassian/ufo';

import { usePaginationController, useFetchErrorController } from '@/controllers';
import {
  defaultMargin,
  flex,
  flexDirectionColumn,
  TABLET_BREAKPOINT,
  SPACE_COMFORTABLE,
  flexEnd,
} from '@/styles';
import { CapabilityList } from '@/components/composed';
import { apiFetch } from '@/services';
import {
  reportCapabilityListExperience,
  reportProductListExperience,
  reportTeamListExperience,
} from '@/common/ufo/experiences';
import { UFOExperienceContext, useRestartUFOComponentExperience } from '@/common/ufo';
import CapabilityListAdvanced from '@/components/composed/capability-list-advanced';
import DashboardSubEntityList from '@/components/composed/dashboard-sub-entity-list';

import Table from './table';
import { ReportProps } from './types';

// eslint-disable-next-line consistent-return
const typeToExperience = (type: ReportProps['type']): UFOExperience => {
  switch (type) {
    case 'capability':
      return reportCapabilityListExperience;
    case 'product':
      return reportProductListExperience;
    case 'team':
      return reportTeamListExperience;
  }
};

const Report: React.FC<ReportProps & {
  currentPage: number;
  onPageChange: (e: SyntheticEvent, n: number) => void;
  experienceSeq: number;
  queryKey: any[];
}> = ({
  copy = { plural: '' },
  type,
  entityType,
  url,
  currentPage,
  onPageChange,
  experienceSeq,
  queryKey,
  isAdvanced,
  ...styles
}) => {
  const { onError } = useFetchErrorController();
  const { isInitialLoading: isLoading, isPreviousData, isFetching, data } = useQuery(
    queryKey,
    () => apiFetch(`${url}&page=${currentPage - 1}`),
    {
      onError,
      keepPreviousData: true,
      enabled: !isAdvanced,
    }
  );

  const pages = isLoading
    ? []
    : Array(Math.ceil(data?.totalPages) || 1)
        .fill(null)
        .map((_, index) => index + 1);
  const content = isLoading ? [] : data?.content;

  const capabilityListNode = isAdvanced ? (
    <CapabilityListAdvanced entityType={entityType} experienceSeq={experienceSeq} />
  ) : (
    <CapabilityList
      isLoading={isLoading}
      rows={content as PageCapabilityDTO['content']}
      experienceSeq={experienceSeq}
      totalElements={data?.totalElements ?? 0}
      entityType={entityType}
    />
  );

  const dashboardSubEntityListNode = isAdvanced ? (
    <DashboardSubEntityList entityType={entityType} experienceSeq={experienceSeq} />
  ) : (
    <Table
      isLoading={isLoading}
      name={copy?.plural}
      type={type as 'product' | 'team'}
      rows={content as API.Team[] | API.Product[]}
      experienceSeq={experienceSeq}
      entityType={entityType}
    />
  );
  return (
    <div
      data-testid="report"
      className="report"
      {...styles}
      css={[
        flex,
        flexDirectionColumn,
        css`
          justify-content: space-between;

          margin-top: ${entityType ? 0 : SPACE_COMFORTABLE}px;

          @media screen and (max-width: ${TABLET_BREAKPOINT}) {
            grid-column: 1 / 3;
          }

          position: relative;
        `,
      ]}
    >
      {type === 'capability' && capabilityListNode}
      {(type === 'product' || type === 'team') && dashboardSubEntityListNode}

      {!isAdvanced && !isLoading && isPreviousData && isFetching && (
        <div
          className="spinneroo"
          css={css`
            position: absolute;
            width: 100%;
            height: 100%;
            background: ${token('elevation.surface', 'white')};
            opacity: 75%;
            display: flex;
            justify-content: center;
            align-items: center;
          `}
        >
          <Spinner size="large" />
        </div>
      )}
      {!isAdvanced && (
        <div css={[flexEnd, defaultMargin]}>
          {(data?.totalPages ?? 0) > 1 && (
            <Pagination selectedIndex={currentPage - 1} pages={pages} onChange={onPageChange} />
          )}
        </div>
      )}
    </div>
  );
};

const ReportInstrumented: React.FC<ReportProps> = ({ type, url, ...props }) => {
  const { currentPage, onPageChange } = usePaginationController();
  const queryKey = [type, 'report', url, currentPage];
  const experience = typeToExperience(type);
  const experienceSeq = useRestartUFOComponentExperience(experience, queryKey);
  return (
    <UFOExperienceContext.Provider value={experience}>
      <Report
        type={type}
        url={url}
        currentPage={currentPage}
        onPageChange={onPageChange}
        experienceSeq={experienceSeq}
        queryKey={queryKey}
        /* eslint-disable-next-line react/jsx-props-no-spreading */
        {...props}
      />
    </UFOExperienceContext.Provider>
  );
};

export default React.memo(ReportInstrumented);
